import { useCallback, useEffect, useState } from 'react';
import { normaliseEligiblePackageStatusCodes } from '@mypoz/shared';

import { fetchEligiblePackageStatusRows } from './catalog-api.js';

export interface EligiblePackageStatusCodes {
  readonly status: 'loading' | 'ready' | 'error';
  /** The vendor's own "orderable" statuses (live: NEW, ARRIVE, IN). Empty until ready. */
  readonly codes: readonly string[];
  readonly retry: () => void;
}

/**
 * `package_status_for_order_list` on its own, for a screen outside the order flow that needs to
 * know which packages can go on an order — the Dashboard's "Ready to order" tile (MYP-27). The
 * order flow reads the same list through `OrderCatalogProvider`, which is scoped to the create and
 * edit routes, so this is one small extra request rather than lifting that whole catalog app-wide.
 */
export function useEligiblePackageStatusCodes(): EligiblePackageStatusCodes {
  const [codes, setCodes] = useState<readonly string[]>([]);
  const [status, setStatus] = useState<EligiblePackageStatusCodes['status']>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    setStatus('loading');
    fetchEligiblePackageStatusRows().then(
      (rows) => {
        if (!current) return;
        setCodes(normaliseEligiblePackageStatusCodes(rows));
        setStatus('ready');
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[orders] could not load the orderable package statuses', cause);
        setStatus('error');
      },
    );
    return () => {
      current = false;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  return { status, codes, retry };
}
